"use client";

import React, { ReactNode, useState, useEffect, Dispatch, SetStateAction } from "react";

import { usePathname } from "next/navigation";

import client from "@/lib/apolloMagentoClient";
import { getCustomerCampaigns } from "@/queries";
import { readCookie } from "@/utils/cookies";

export interface CustomerCampaign {
  // Define actual campaign  here if known, else keep as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface Customer {
  // Define the customer shape here if known, else keep as any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface UserContextType {
  isAuthLoading: boolean;
  setAuthLoading: Dispatch<SetStateAction<boolean>>;
  isLoggedIn: boolean;
  site: string;
  setSite: Dispatch<SetStateAction<string>>;
  customer: Customer | null;
  customerCampaigns?: CustomerCampaign[] | null;
  fetchCustomerCampaigns: () => void;
  skipClientCalls: boolean;
  setSkipClientCalls: Dispatch<SetStateAction<boolean>>;
  client: typeof client;
  logUserOut: () => void;
  logUserIn: (customer?: Customer | null) => void;
}

export const defaultState: UserContextType = {
  isAuthLoading: false,
  setAuthLoading: () => {},
  isLoggedIn: false,
  site: "",
  setSite: () => {},
  customer: null,
  customerCampaigns: null,
  fetchCustomerCampaigns: () => {},
  skipClientCalls: false,
  setSkipClientCalls: () => {},
  client,
  logUserOut: () => {},
  logUserIn: () => {},
};

const UserContext = React.createContext<UserContextType>(defaultState);

interface UserProviderProps {
  children: ReactNode;
}

const UserProvider = ({ children }: UserProviderProps) => {
  const pathname = usePathname();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  // Always start false so server and client first paint match (avoids hydration mismatch).
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [skipClientCalls, setSkipClientCalls] = useState(false);
  const [site, setSite] = useState("");
  const [customerCampaigns, setCustomerCampaigns] = useState<CustomerCampaign[] | null>(null);
  const isCheckout = pathname?.startsWith("/checkout") ?? false;

  useEffect(() => {
    if (typeof window !== "undefined" && readCookie("gatsbytoken")) {
      setIsLoggedIn(true);
    }
  }, []);

  const fetchCustomerCampaigns = () => {
    client
      .query({
        query: getCustomerCampaigns,
        fetchPolicy: "network-only",
      })
      .then((result) => {
        setCustomerCampaigns(result?.data?.CustomerCampaigns || []);
      })
      .catch(() => {
        // handle error if needed
      });
  };

  useEffect(() => {
    if (isLoggedIn && !skipClientCalls && !isCheckout) {
      fetchCustomerCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isCheckout]);

  const logUserOut = () => {
    setIsLoggedIn(false);
    setCustomer(null);
  };

  const logUserIn = (c: Customer | null = null) => {
    setIsLoggedIn(true);
    if (c) {
      setCustomer(c);
    }
  };

  return (
    <UserContext.Provider
      value={{
        isAuthLoading,
        setAuthLoading: setIsAuthLoading,
        isLoggedIn,
        site,
        setSite,
        customer,
        customerCampaigns,
        fetchCustomerCampaigns,
        skipClientCalls,
        setSkipClientCalls,
        client,
        logUserOut,
        logUserIn,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
export { UserProvider };
